<template>
  <Dialog
    v-model:visible="visible"
    :style="{width: '80vw'}"
    :modal="true"
    :header="tituloModal"
    class="p-fluid mhc-dialog"
    @hide="resetModal"
  >
    <div class="mhc-body">

      <!-- ══ PASO 1 — FORMULARIO ══ -->
      <div v-if="paso === 1">

        <!-- Banner en proceso -->
        <div v-if="enProceso" class="mhc-en-proceso">
          <div class="mhc-en-proceso-header">
            <div class="mhc-en-proceso-icon"><i class="pi pi-spin pi-spinner"></i></div>
            <div>
              <div class="mhc-en-proceso-titulo">Procesando hoja de vida</div>
              <div class="mhc-en-proceso-sub">
                {{ sincronizacion?.mensaje || 'La hoja de vida está siendo generada. Esto puede tardar entre 1 y 3 minutos.' }}
              </div>
            </div>
          </div>
          <ProgressBar mode="indeterminate" style="height:4px;border-radius:2px;margin-top:1rem"/>
          <div class="mhc-en-proceso-chips">
            <span class="mhc-chip"><i class="pi pi-clock mr-1"></i>Consulta ID: {{ sincronizacion?.consulta_id }}</span>
            <span class="mhc-chip"><i class="pi pi-refresh mr-1"></i>Intento {{ pollingIntento }} / {{ pollingMax }}</span>
            <span class="mhc-chip">Verificando cada 40 segundos…</span>
          </div>
          <div class="mhc-en-proceso-info">
            <i class="pi pi-info-circle mr-2"></i>
            No cierres esta ventana. Los datos se cargarán automáticamente cuando estén listos.
          </div>
        </div>

        <!-- Alerta ya existe -->
        <div v-if="yaExiste && !enProceso" class="mhc-alert-warn">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          Este conductor ya está registrado en el sistema.
        </div>

        <!-- Card formulario -->
        <div class="mhc-form-card" :class="modoResincronizar ? 'mhc-form-card--resinc' : ''">
          <div class="mhc-form-header">
            <i :class="modoResincronizar ? 'pi pi-refresh' : 'pi pi-user-plus'"></i>
            <div>
              <div class="mhc-form-titulo">
                {{ modoResincronizar ? 'Resincronización de Hoja de Vida' : 'Datos del conductor' }}
              </div>
              <div class="mhc-form-sub">
                <template v-if="modoResincronizar">
                  Confirma los datos para re-consultar la hoja de vida del conductor
                  <strong>{{ documentoInicial }}</strong>.
                </template>
                <template v-else>
                  Ingresa el documento para consultar las fuentes externas (RUNT, SIMIT, RUAF, Policía).
                </template>
              </div>
            </div>
          </div>

          <div class="mhc-form-grid mt-3">
            <!-- Tipo documento -->
            <div class="mhc-form-field">
              <label>Tipo de Documento <span class="mhc-required">*</span></label>
              <Dropdown
                v-model="frm.tipo_documento"
                :options="tiposDocumento"
                optionLabel="label" optionValue="value"
                :disabled="modoResincronizar"
                :class="{ 'p-invalid': submitted && !frm.tipo_documento }"
              />
              <small class="mhc-error" v-if="submitted && !frm.tipo_documento">Requerido</small>
              <small v-if="modoResincronizar" class="mhc-hint-lock">
                <i class="pi pi-lock mr-1"></i>Fijado desde el registro existente
              </small>
            </div>

            <!-- Número documento -->
            <div class="mhc-form-field">
              <label>Número de Documento <span class="mhc-required">*</span></label>
              <InputText
                v-model="frm.documento"
                :disabled="modoResincronizar"
                :class="{ 'p-invalid': submitted && !frm.documento }"
                @keydown.enter="!enProceso && !modoResincronizar && consultar()"
              />
              <small class="mhc-error" v-if="submitted && !frm.documento">Requerido</small>
              <small v-if="modoResincronizar" class="mhc-hint-lock">
                <i class="pi pi-lock mr-1"></i>Fijado desde el registro existente
              </small>
            </div>

            <!-- Fecha expedición -->
            <div class="mhc-form-field">
              <label>Fecha de Expedición</label>
              <Calendar
                v-model="frm.fecha_expedicion"
                dateFormat="yy-mm-dd"
                :showIcon="true"
                :maxDate="hoy"
                :disabled="modoResincronizar"
              />
              <small v-if="modoResincronizar && fechaExpedicionInicial" class="mhc-hint-lock">
                <i class="pi pi-lock mr-1"></i>Guardada desde la primera consulta
              </small>
              <small v-else-if="modoResincronizar && !fechaExpedicionInicial" class="mhc-hint-gray">
                <i class="pi pi-info-circle mr-1"></i>No registrada en la primera consulta
              </small>
              <small v-else class="mhc-hint-gray">
                <i class="pi pi-info-circle mr-1"></i>Opcional. Mejora la precisión de la consulta.
              </small>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="consultando" class="mhc-loading">
            <ProgressBar mode="indeterminate" style="height:3px;border-radius:2px"/>
            <div class="mhc-loading-text">
              <i class="pi pi-spin pi-spinner mr-2"></i>
              {{ modoResincronizar ? 'Re-consultando fuentes externas…' : 'Consultando fuentes externas (RUNT, SIMIT, RUAF, Policía)…' }}
            </div>
          </div>

          <!-- Error -->
          <div v-if="errorConsulta" class="mhc-error-card">
            <i class="pi pi-times-circle mr-2"></i>
            {{ errorConsulta }}
            <div class="mt-2">
              <button class="mhc-btn-outline mhc-btn-sm" @click="consultar">
                <i class="pi pi-refresh mr-1"></i> Reintentar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ PASO 2 — VISTA PREVIA ══ -->
      <div v-if="paso === 2 && apiResponse">

        <!-- Header conductor -->
        <div class="mhc-conductor-header mb-3">
          <div class="mhc-conductor-foto">
            <div class="mhc-conductor-foto-placeholder"><i class="pi pi-user"></i></div>
          </div>
          <div class="mhc-conductor-main">
            <div class="mhc-doc-badge">{{ frm.documento }}</div>
            <div class="mhc-conductor-nombre">{{ nombreConductor }}</div>
            <div class="mhc-conductor-sub">{{ nombres }} {{ apellidos }} · Estado: {{ runtpPersona?.estado_conductor ?? '—' }}</div>
          </div>
          <div class="mhc-conductor-badges">
            <div class="mhc-badge-item">
              <span class="mhc-badge-lbl">Licencia</span>
              <span :class="['mhc-status', badgeLicencia.cls]">{{ badgeLicencia.label }}</span>
            </div>
            <div class="mhc-badge-item">
              <span class="mhc-badge-lbl">SIMIT</span>
              <span :class="['mhc-count', badgeSimit===0?'mhc-count--ok':'mhc-count--danger']">
                {{ badgeSimit === 0 ? 'Sin deuda' : badgeSimit }}
              </span>
            </div>
            <div class="mhc-badge-item">
              <span class="mhc-badge-lbl">Antecedentes</span>
              <span :class="['mhc-status', badgeAntecedentes?'mhc-status--danger':'mhc-status--ok']">
                {{ badgeAntecedentes ? 'SÍ' : 'NO' }}
              </span>
            </div>
            <div class="mhc-badge-item">
              <span class="mhc-badge-lbl">Estado</span>
              <span :class="['mhc-status', runtpPersona?.estado_conductor==='ACTIVO'?'mhc-status--ok':'mhc-status--warn']">
                {{ runtpPersona?.estado_conductor ?? '—' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <TabView>

          <!-- Datos Personales -->
          <TabPanel>
            <template #header><i class="pi pi-user mr-2"></i>Datos Personales</template>
            <div class="mhc-seccion-grid">

              <div class="mhc-seccion">
                <div class="mhc-seccion-titulo"><i class="pi pi-id-card"></i> Identificación</div>
                <div class="mhc-kv-grid">
                  <span class="mhc-kv-lbl">Documento</span>     <span class="mhc-mono mhc-bold">{{ frm.documento }}</span>
                  <span class="mhc-kv-lbl">Nombre completo</span><span class="mhc-bold">{{ nombreConductor }}</span>
                  <span class="mhc-kv-lbl">Nombres</span>        <span>{{ nombres }}</span>
                  <span class="mhc-kv-lbl">Apellidos</span>      <span>{{ apellidos }}</span>
                  <span class="mhc-kv-lbl">Identificación</span> <span class="mhc-mono">{{ informacionBasica?.numero_de_identificacion || '—' }}</span>
                </div>
              </div>

              <div class="mhc-seccion">
                <div class="mhc-seccion-titulo"><i class="pi pi-check-circle"></i> Estado RUNT</div>
                <div class="mhc-kv-grid">
                  <span class="mhc-kv-lbl">Estado persona</span>
                  <span><span :class="['mhc-status', runtpPersona?.estado_persona==='ACTIVA'?'mhc-status--ok':'mhc-status--warn']">{{ runtpPersona?.estado_persona || '—' }}</span></span>
                  <span class="mhc-kv-lbl">Estado conductor</span>
                  <span><span :class="['mhc-status', runtpPersona?.estado_conductor==='ACTIVO'?'mhc-status--ok':'mhc-status--warn']">{{ runtpPersona?.estado_conductor || '—' }}</span></span>
                  <span class="mhc-kv-lbl">N° Inscripción</span>   <span class="mhc-mono">{{ runtpPersona?.numero_inscripcion || '—' }}</span>
                  <span class="mhc-kv-lbl">Fecha inscripción</span><span>{{ runtpPersona?.fecha_inscripcion || '—' }}</span>
                </div>
              </div>

              <div class="mhc-seccion" v-if="dataJson?.proc">
                <div class="mhc-seccion-titulo"><i class="pi pi-verified"></i> Contraloría (PROC)</div>
                <div class="mhc-kv-grid">
                  <span class="mhc-kv-lbl">Estado</span>       <span style="font-size:.75rem">{{ dataJson.proc.estado || '—' }}</span>
                  <span class="mhc-kv-lbl">Sanciones</span>
                  <span><span :class="['mhc-status', dataJson.proc.tiene_sanciones?'mhc-status--danger':'mhc-status--ok']">{{ dataJson.proc.tiene_sanciones?'SÍ':'NO' }}</span></span>
                  <span class="mhc-kv-lbl">N° Certificado</span><span class="mhc-mono">{{ dataJson.proc.nro_certificado || '—' }}</span>
                  <span class="mhc-kv-lbl">Firmado por</span>   <span style="font-size:.75rem">{{ dataJson.proc.firmado_por || '—' }}</span>
                </div>
              </div>

            </div>
          </TabPanel>

          <!-- Licencias -->
          <TabPanel>
            <template #header><i class="pi pi-car mr-2"></i>Licencias de Conducción</template>
            <div v-if="!licencias.length" class="mhc-empty-tab">
              <i class="pi pi-times-circle"></i> Sin licencias registradas
            </div>
            <div v-for="(lic,i) in licencias" :key="i"
              class="mhc-poliza-card" :class="lic.estado==='ACTIVA'?'mhc-poliza-vigente':''">
              <div class="mhc-poliza-header">
                <span class="mhc-poliza-entidad">{{ lic.ot_expide_lic }}</span>
                <span :class="['mhc-status', lic.estado==='ACTIVA'?'mhc-status--ok':'mhc-status--sec']">{{ lic.estado }}</span>
              </div>
              <div class="mhc-poliza-kv">
                <div><span class="mhc-kv-lbl">N° Licencia</span>   <span class="mhc-mono">{{ lic.nro_licencia }}</span></div>
                <div><span class="mhc-kv-lbl">Categoría</span>     <span>{{ lic.categoria || '—' }}</span></div>
                <div><span class="mhc-kv-lbl">Expedición</span>    <span>{{ lic.fecha_expedicion }}</span></div>
                <div><span class="mhc-kv-lbl">Retención</span>     <span>{{ lic.retencion || '—' }}</span></div>
                <div><span class="mhc-kv-lbl">Restricciones</span> <span>{{ lic.restricciones || 'Ninguna' }}</span></div>
              </div>
            </div>
          </TabPanel>

          <!-- Seguridad Social -->
          <TabPanel>
            <template #header><i class="pi pi-heart mr-2"></i>Seguridad Social</template>
            <div class="mhc-seccion-grid">
              <div class="mhc-seccion" v-if="saludList.length">
                <div class="mhc-seccion-titulo"><i class="pi pi-plus-circle"></i> Salud</div>
                <div v-for="(s,i) in saludList" :key="i" class="mhc-kv-grid mb-2">
                  <span class="mhc-kv-lbl">Administradora</span><span style="font-size:.75rem">{{ s.administradora }}</span>
                  <span class="mhc-kv-lbl">Régimen</span>       <span>{{ s.regimen }}</span>
                  <span class="mhc-kv-lbl">Tipo afiliado</span> <span>{{ s.tipo_de_afiliado || '—' }}</span>
                  <span class="mhc-kv-lbl">Estado</span>
                  <span><span :class="['mhc-status', s.estado_de_afiliacion==='Activo'?'mhc-status--ok':'mhc-status--warn']">{{ s.estado_de_afiliacion }}</span></span>
                  <span class="mhc-kv-lbl">Municipio</span>     <span>{{ s.departamento_municipio }}</span>
                </div>
              </div>
              <div class="mhc-seccion" v-if="pensionesList.length">
                <div class="mhc-seccion-titulo"><i class="pi pi-wallet"></i> Pensiones</div>
                <div v-for="(p,i) in pensionesList" :key="i" class="mhc-kv-grid mb-2">
                  <span class="mhc-kv-lbl">Administradora</span><span style="font-size:.75rem">{{ p.administradora }}</span>
                  <span class="mhc-kv-lbl">Régimen</span>       <span>{{ p.regimen }}</span>
                  <span class="mhc-kv-lbl">Estado</span>
                  <span><span :class="['mhc-status', p.estado_de_afiliacion?.toLowerCase().includes('activ')?'mhc-status--ok':'mhc-status--warn']">{{ p.estado_de_afiliacion }}</span></span>
                </div>
              </div>
              <div class="mhc-seccion" v-if="arlList.length">
                <div class="mhc-seccion-titulo"><i class="pi pi-shield"></i> Riesgos Laborales (ARL)</div>
                <div v-for="(r,i) in arlList" :key="i" class="mhc-kv-grid mb-2">
                  <span class="mhc-kv-lbl">Administradora</span><span style="font-size:.75rem">{{ r.administradora }}</span>
                  <span class="mhc-kv-lbl">Municipio</span>     <span>{{ r.municipio_labora }}</span>
                  <span class="mhc-kv-lbl">Estado</span>
                  <span><span :class="['mhc-status', r.estado_de_afiliacion==='Activa'?'mhc-status--ok':'mhc-status--warn']">{{ r.estado_de_afiliacion }}</span></span>
                </div>
              </div>
              <div v-if="!saludList.length && !pensionesList.length && !arlList.length" class="mhc-empty-tab">
                <i class="pi pi-times-circle"></i> Sin datos de seguridad social
              </div>
            </div>
          </TabPanel>

          <!-- SIMIT -->
          <TabPanel>
            <template #header>
              <i class="pi pi-exclamation-triangle mr-2"></i>SIMIT
              <span v-if="badgeSimit > 0" class="mhc-tab-badge">{{ badgeSimit }}</span>
            </template>
            <div v-if="badgeSimit === 0" class="mhc-simit-clean">
              <i class="pi pi-check-circle mhc-simit-icon"></i>
              <strong>¡Sin comparendos!</strong>
              <p>{{ dataJson?.simit?.estado_general }}</p>
            </div>
            <template v-else>
              <div class="mhc-deuda-resumen mb-3">
                <div class="mhc-deuda-item">
                  <span class="mhc-kv-lbl">Comparendos</span>
                  <span class="mhc-count mhc-count--danger">{{ badgeSimit }}</span>
                </div>
                <div class="mhc-deuda-item">
                  <span class="mhc-kv-lbl">Multas</span>
                  <span class="mhc-count mhc-count--warn">{{ dataJson?.simit?.resumen?.multas ?? 0 }}</span>
                </div>
                <div class="mhc-deuda-item">
                  <span class="mhc-kv-lbl">Total a pagar</span>
                  <strong class="mhc-deuda-total">{{ fmtCOP(dataJson?.simit?.resumen?.total_pagar) }}</strong>
                </div>
              </div>
              <table class="mhc-tabla">
                <thead><tr>
                  <th style="width:105px">Fecha</th>
                  <th>Secretaría</th>
                  <th>Estado</th>
                  <th style="width:120px">Valor</th>
                </tr></thead>
                <tbody>
                  <tr v-for="(c,i) in dataJson?.simit?.comparendos ?? []" :key="i">
                    <td>{{ c.fecha_imposicion }}</td>
                    <td><span class="mhc-sub">{{ c.secretaria }}</span></td>
                    <td>
                      <span :class="['mhc-status', c.estado?.toLowerCase().includes('coactivo')?'mhc-status--danger':'mhc-status--warn']">
                        {{ c.estado }}
                      </span>
                    </td>
                    <td><span class="mhc-monto">{{ fmtCOP(c.valor_cop) }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div class="mhc-nota mt-2">
                <i class="pi pi-info-circle"></i>
                El valor total consolidado aparece en el resumen. Los valores individuales corresponden al acumulado del SIMIT.
              </div>
            </template>
          </TabPanel>

          <!-- Antecedentes -->
          <TabPanel>
            <template #header><i class="pi pi-shield mr-2"></i>Antecedentes</template>
            <div class="mhc-seccion-grid">
              <div class="mhc-seccion">
                <div class="mhc-seccion-titulo"><i class="pi pi-shield"></i> Policía Nacional</div>
                <div class="mhc-kv-grid">
                  <span class="mhc-kv-lbl">Antecedentes</span>
                  <span><span :class="['mhc-status', dataJson?.policia?.tiene_antecedentes?'mhc-status--danger':'mhc-status--ok']">{{ dataJson?.policia?.tiene_antecedentes?'SÍ':'NO' }}</span></span>
                  <span class="mhc-kv-lbl">Tipo doc.</span><span class="mhc-mono">{{ dataJson?.policia?.tipo_doc || '—' }}</span>
                </div>
              </div>
              <div class="mhc-seccion">
                <div class="mhc-seccion-titulo"><i class="pi pi-lock"></i> Contraloría</div>
                <div class="mhc-kv-grid">
                  <span class="mhc-kv-lbl">Reportado</span>
                  <span><span :class="['mhc-status', dataJson?.contra?.reportado?'mhc-status--danger':'mhc-status--ok']">{{ dataJson?.contra?.reportado?'SÍ':'NO' }}</span></span>
                  <span class="mhc-kv-lbl">Estado</span>          <span>{{ dataJson?.contra?.estado || 'Sin sanción' }}</span>
                  <span class="mhc-kv-lbl">Tipo documento</span>  <span>{{ dataJson?.contra?.tipo_documento || '—' }}</span>
                  <span class="mhc-kv-lbl">Cód. verificación</span>
                  <span class="mhc-mono" style="font-size:.7rem;word-break:break-all">{{ dataJson?.contra?.codigo_verificacion || '—' }}</span>
                </div>
              </div>
              <div class="mhc-seccion" v-if="dataJson?.proc">
                <div class="mhc-seccion-titulo"><i class="pi pi-file"></i> PROC — Certificado</div>
                <div class="mhc-kv-grid">
                  <span class="mhc-kv-lbl">Certificado</span>  <span class="mhc-mono">{{ dataJson.proc.nro_certificado }}</span>
                  <span class="mhc-kv-lbl">Fecha</span>         <span>{{ fmtFechaISO(dataJson.proc.fecha_expedicion) }}</span>
                  <span class="mhc-kv-lbl">Firmado por</span>   <span style="font-size:.75rem">{{ dataJson.proc.firmado_por }}</span>
                  <span class="mhc-kv-lbl">Cargo</span>         <span style="font-size:.75rem">{{ dataJson.proc.cargo_firmante }}</span>
                  <span class="mhc-kv-lbl">Sanciones</span>
                  <span><span :class="['mhc-status', dataJson.proc.tiene_sanciones?'mhc-status--danger':'mhc-status--ok']">{{ dataJson.proc.tiene_sanciones?'SÍ':'NO' }}</span></span>
                </div>
              </div>
            </div>
          </TabPanel>

          <!-- Documentos y Fotos -->
          <TabPanel>
            <template #header>
              <i class="pi pi-images mr-2"></i>Documentos y Fotos
              <span v-if="fotosDocumento.length" class="mhc-tab-badge mhc-tab-badge--blue">
                {{ fotosDocumento.length }}
              </span>
            </template>

            <div v-if="!fotosDocumento.length" class="mhc-empty-tab">
              <i class="pi pi-times-circle"></i> Sin fotos disponibles para este conductor
            </div>

            <div v-else class="mhc-fotos-grid">
              <div
                v-for="foto in fotosDocumento"
                :key="foto.key"
                class="mhc-foto-card"
              >
                <!-- Cabecera de la card -->
                <div class="mhc-foto-card-header">
                  <i :class="['pi', foto.icono, 'mr-2']"></i>
                  <span>{{ foto.label }}</span>
                </div>

                <!-- Estado: ya cargada ✓ -->
                <div v-if="foto.urlPublica" class="mhc-foto-preview">
                  <img :src="foto.urlPublica" :alt="foto.label" class="mhc-foto-img" />
                  <a :href="foto.urlPublica" target="_blank" class="mhc-foto-open">
                    <i class="pi pi-external-link mr-1"></i>Abrir en nueva pestaña
                  </a>
                </div>

                <!-- Estado: cargando -->
                <div v-else-if="foto.loading" class="mhc-foto-placeholder mhc-foto-placeholder--loading">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Cargando imagen…</span>
                </div>

                <!-- Estado: error -->
                <div v-else-if="foto.error" class="mhc-foto-placeholder mhc-foto-placeholder--error">
                  <i class="pi pi-times-circle"></i>
                  <span>{{ foto.error }}</span>
                  <button class="mhc-btn-outline mhc-btn-sm mt-2" @click="verFoto(foto.key, foto.urlEncriptada)">
                    <i class="pi pi-refresh mr-1"></i> Reintentar
                  </button>
                </div>

                <!-- Estado: sin cargar — click para ver -->
                <div
                  v-else
                  class="mhc-foto-placeholder mhc-foto-placeholder--clickable"
                  @click="verFoto(foto.key, foto.urlEncriptada)"
                >
                  <i class="pi pi-eye"></i>
                  <span>Clic para ver</span>
                  <small>La imagen se carga bajo demanda</small>
                </div>

              </div>
            </div>

            <div v-if="fotosDocumento.length" class="mhc-nota mt-3">
              <i class="pi pi-info-circle"></i>
              Las imágenes se cargan individualmente al hacer clic. No se almacenan en el sistema.
            </div>
          </TabPanel>

        </TabView>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="mhc-footer">
        <button v-if="paso === 2" class="mhc-btn-outline"
          @click="paso = 1; apiResponse = null; errorConsulta = ''">
          <i class="pi pi-arrow-left mr-1"></i> Volver
        </button>
        <span v-else></span>
        <div class="mhc-footer-right">
          <button class="mhc-btn-outline" @click="cerrar">
            <i class="pi pi-times mr-1"></i> Cancelar
          </button>
          <button v-if="paso === 1" class="mhc-btn-action"
            :class="modoResincronizar ? 'mhc-btn-action--warn' : 'mhc-btn-action--navy'"
            :disabled="!frm.tipo_documento || !frm.documento || enProceso || consultando"
            @click="consultar">
            <i :class="consultando ? 'pi pi-spin pi-spinner' : modoResincronizar ? 'pi pi-refresh' : 'pi pi-search'" class="mr-1"></i>
            {{ modoResincronizar ? 'Resincronizar' : 'Consultar' }}
          </button>
          <button v-if="paso === 2 && !modoResincronizar" class="mhc-btn-action mhc-btn-action--navy"
            :disabled="guardando" @click="guardar">
            <i :class="guardando ? 'pi pi-spin pi-spinner' : 'pi pi-save'" class="mr-1"></i>
            Guardar Conductor
          </button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import hvPollingMixin from '../../../mixins/hvPollingMixin.js';

export default {
  name: 'ModalNuevoConductor',

  mixins: [hvPollingMixin],

  props: {
    modelValue:             { type: Boolean,         default: false },
    idRol:                  { type: [Number, String], required: true },
    idCustomer:             { type: [Number, String, null], default: null },
    documentoInicial:       { type: String,           default: '' },
    tipoDocumentoInicial:   { type: Number,           default: 1  },
    fechaExpedicionInicial: { type: [String, null],   default: null },
    modoResincronizar:      { type: Boolean,          default: false },
  },

  emits: ['update:modelValue', 'guardado'],

  data() {
    const fechaInicial = (() => {
      const f = this.fechaExpedicionInicial;
      if (this.modoResincronizar && f && f !== 'null' && f.includes('-')) {
        const [y, m, d] = String(f).split('-');
        return new Date(Number(y), Number(m) - 1, Number(d));
      }
      return null;
    })();

    return {
      paso:          1,
      submitted:     false,
      consultando:   false,
      guardando:     false,
      yaExiste:      false,
      errorConsulta: '',
      apiResponse:   null,
      hoy:           new Date(),
      enProceso:      false,
      sincronizacion: null,

      frm: {
        tipo_documento:   this.modoResincronizar ? (this.tipoDocumentoInicial ?? 1) : 1,
        documento:        this.modoResincronizar ? (this.documentoInicial ?? '')    : '',
        fecha_expedicion: fechaInicial,
      },

      frmEditable: { email: '', celular: '' },

      tiposDocumento: [
        { label: 'CC — Cédula de Ciudadanía',     value: 1 },
        { label: 'TI — Tarjeta de Identidad',      value: 2 },
        { label: 'CE — Cédula de Extranjería',     value: 3 },
        { label: 'PA — Pasaporte',                 value: 4 },
        { label: 'NIT',                            value: 5 },
        { label: 'CD — Carnet Diplomático',        value: 6 },
        { label: 'RC — Registro Civil',            value: 7 },
        { label: 'PPT — Permiso Protección Temp.', value: 8 },
      ],
      fotosPublicas: {
          Foto_conductor_url:                       null,
          Foto_cedula_frontal_url:                  null,
          foto_cedula_anverso_url:                  null,
          foto_licencia_de_conduccion_frontal_url:  null,
          foto_licencia_de_conduccion_anverso_url:  null,
      },
      fotosLoading: {
          Foto_conductor_url:                       false,
          Foto_cedula_frontal_url:                  false,
          foto_cedula_anverso_url:                  false,
          foto_licencia_de_conduccion_frontal_url:  false,
          foto_licencia_de_conduccion_anverso_url:  false,
      },
      fotosError: {},
    };
  },

  computed: {
    visible: {
      get() { return this.modelValue; },
      set(v) { this.$emit('update:modelValue', v); },
    },

    tituloModal() {
      if (this.modoResincronizar) {
        return this.paso === 1
          ? `Resincronizar Conductor — ${this.documentoInicial}`
          : `Hoja de Vida Actualizada — ${this.nombreConductor}`;
      }
      return this.paso === 1 ? 'Consultar / Registrar Conductor' : `Hoja de Vida — ${this.nombreConductor}`;
    },

    dataJson() { return this.apiResponse?.data_json ?? null; },

    nombreConductor() {
      if (!this.apiResponse) return '—';
      return this.dataJson?.runtp?.datos_persona?.nombre_completo ?? '—';
    },

    informacionBasica() {
      const ruaf = this.dataJson?.ruaf;
      if (!ruaf) return null;
      return ruaf.informacion_basica?.[0] ?? ruaf.informacion_basica?.filas?.[0] ?? null;
    },

    nombres() {
      const b = this.informacionBasica;
      if (!b) return '—';
      return [b.primer_nombre, b.segundo_nombre].filter(Boolean).join(' ') || '—';
    },

    apellidos() {
      const b = this.informacionBasica;
      if (!b) return '—';
      return [b.primer_apellido, b.segundo_apellido].filter(Boolean).join(' ') || '—';
    },

    runtpPersona() { return this.dataJson?.runtp?.datos_persona ?? null; },

    licencias() {
      const sec = this.dataJson?.runtp?.secciones?.licencias_de_conduccion;
      return sec?.registros ?? sec?.filas ?? [];
    },

    badgeLicencia() {
      if (!this.licencias.length) return { label:'Sin datos', cls:'mhc-status--sec' };
      if (this.licencias.find(l => l.estado==='ACTIVA'))   return { label:'ACTIVA',   cls:'mhc-status--ok' };
      if (this.licencias.find(l => l.estado==='RETENIDA')) return { label:'RETENIDA', cls:'mhc-status--danger' };
      return { label:'VENCIDA', cls:'mhc-status--warn' };
    },

    badgeSimit() { return this.dataJson?.simit?.resumen?.comparendos ?? 0; },

    badgeAntecedentes() {
      return this.dataJson?.policia?.tiene_antecedentes === true
          || this.dataJson?.contra?.reportado === true;
    },

    saludList() {
      const s = this.dataJson?.ruaf?.afiliacion_a_salud;
      if (!s) return [];
      return Array.isArray(s) ? s : (s.filas ?? []);
    },

    pensionesList() {
      const p = this.dataJson?.ruaf?.afiliacion_a_pensiones;
      if (!p) return [];
      return Array.isArray(p) ? p : (p.filas ?? []);
    },

    arlList() {
      const r = this.dataJson?.ruaf?.afiliacion_a_riesgos_laborales;
      if (!r) return [];
      return Array.isArray(r) ? r : (r.filas ?? []);
    },
    fotosDocumento() {
        if (!this.apiResponse) return [];
        return [
            { key: 'Foto_conductor_url',                      label: 'Foto del Conductor',          icono: 'pi-user'      },
            { key: 'Foto_cedula_frontal_url',                 label: 'Cédula — Frontal',            icono: 'pi-id-card'   },
            { key: 'foto_cedula_anverso_url',                 label: 'Cédula — Reverso',            icono: 'pi-id-card'   },
            { key: 'foto_licencia_de_conduccion_frontal_url', label: 'Licencia — Frontal',          icono: 'pi-car'       },
            { key: 'foto_licencia_de_conduccion_anverso_url', label: 'Licencia — Reverso',          icono: 'pi-car'       },
        ].map(f => ({
            ...f,
            urlEncriptada: this.apiResponse[f.key] ?? null,
            urlPublica:    this.fotosPublicas[f.key],
            loading:       this.fotosLoading[f.key],
            error:         this.fotosError[f.key] ?? null,
        })).filter(f => f.urlEncriptada); // Solo mostrar las que vienen en la respuesta
    },
  },

  methods: {
    async consultar() {
        this.submitted = true;
        if (!this.frm.tipo_documento || !this.frm.documento?.trim()) return;

        this.consultando    = true;
        this.errorConsulta  = '';
        this.apiResponse    = null;
        this.yaExiste       = false;
        this.enProceso      = false;
        this.sincronizacion = null;

        const cookie = {};
        document.cookie.split(';').forEach(el => {
            const [k, v] = el.split('=');
            cookie[k.trim()] = decodeURIComponent(v ?? '');
        });

        let fechaStr = null;
        if (this.frm.fecha_expedicion) {
            const d = new Date(this.frm.fecha_expedicion);
            fechaStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }

        try {
            const res = await window.api.call('post', '/registrar_conductor_hv', {
            email:            cookie['Email'],
            id_rol:           this.idRol,
            id_customer:      this.idCustomer,
            tipo_documento:   this.frm.tipo_documento,
            documento:        this.frm.documento.trim(),
            fecha_expedicion: fechaStr,
            });

            if (res.status === 200) {
            // ── En proceso ─────────────────────────────────────────────────
            if (res.data?.en_proceso) {
                this.enProceso      = true;
                this.sincronizacion = res.data.sincronizacion;
                this.yaExiste       = res.data.ya_existe ?? false;
                this.iniciarPollingSolicitudHv('driver', this.frm.documento.trim());
                return;
            }

            // ── Datos completos ────────────────────────────────────────────
            const hv = res.data?.data?.hoja_de_vida_conductor ?? null;
            if (!hv || !hv.data_json) {
                this.errorConsulta = 'No se encontró información. Verifica los datos o intenta sin la fecha de expedición.';
                return;
            }
            this.apiResponse = hv;
            this.frmEditable = { email: '', celular: '' };
            this.yaExiste    = res.data?.ya_existe ?? false;
            this.paso        = 2;
            }

        } catch (err) {
            const status   = err?.status ?? err?.response?.status ?? 0;
            const errorKey = err?.data?.error ?? err?.response?.data?.error ?? '';
            const msg      = err?.data?.message ?? err?.response?.data?.message
                        ?? err?.data?.error   ?? err?.response?.data?.error
                        ?? '';

            // ── Token expirado / revocado ───────────────────────────────────
            if (status === 401 || errorKey === 'token_expirado') {
            this.$toast.add({
                severity: 'error',
                summary:  'Servicio no disponible',
                detail:   msg || 'El token de consulta ha expirado o fue revocado. Comuníquese con el administrador.',
                life:     12000,
            });
            return;
            }

            // ── Datos no corresponden al conductor ──────────────────────────
            if (status === 422 || errorKey === 'datos_inconsistentes') {
            this.$toast.add({
                severity: 'warn',
                summary:  'Datos no corresponden',
                detail:   msg || 'Los datos ingresados no corresponden con el conductor consultado.',
                life:     10000,
            });
            return;
            }

            // ── Ya hay una solicitud en proceso para este documento ─────────
            if (status === 409 || errorKey === 'solicitud_en_proceso') {
            const body = err?.data ?? err?.response?.data ?? {};
            this.enProceso      = true;
            this.sincronizacion = {
                mensaje:     msg || 'La hoja de vida está en proceso de generación.',
                consulta_id: body.consulta_id,
            };
            this.iniciarPollingSolicitudHv('driver', this.frm.documento.trim());
            return;
            }

            // ── Errores HTTP conocidos ──────────────────────────────────────
            if (status === 404)      this.errorConsulta = 'No se encontró información para este documento.';
            else if (status === 408) this.errorConsulta = 'La consulta tardó demasiado. Intenta de nuevo.';
            else                     this.errorConsulta = msg || 'Error al consultar. Intenta de nuevo o contacta soporte.';

        } finally {
            this.consultando = false;
        }
        },

    // ── Callbacks del hvPollingMixin ──────────────────────────────────────
    async onSolicitudHvCompletada() {
      this.enProceso = false;
      // Ya no hay bloqueo activo: reconsultamos una vez para traer los datos completos.
      await this.consultar();
      if (this.paso === 2) {
        this.$toast.add({ severity:'success', summary:'Hoja de vida lista', detail:'Los datos del conductor han sido cargados.', life:3000 });
      }
    },

    onSolicitudHvError(mensaje) {
      this.enProceso = false;
      this.errorConsulta = mensaje || 'No se pudo generar la hoja de vida. Intenta de nuevo.';
    },

    onSolicitudHvTimeout() {
      this.enProceso = false;
      this.errorConsulta = 'La consulta está tardando demasiado. Intenta de nuevo en unos minutos.';
    },

    async guardar() {
      this.guardando = true;
      const cookie = {};
      document.cookie.split(';').forEach(el => { const [k,v]=el.split('='); cookie[k.trim()]=decodeURIComponent(v??''); });

      try {
        let fechaStr = null;
        if (this.frm.fecha_expedicion) {
          const d = new Date(this.frm.fecha_expedicion);
          fechaStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
        const res = await window.api.call('post', '/guardar_conductor_hv', {
          email: cookie['Email'], id_rol: this.idRol, id_customer: this.idCustomer,
          documento: this.frm.documento.trim(),
          tipo_documento: this.frm.tipo_documento,
          fecha_expedicion: fechaStr,
          email_conductor: this.frmEditable.email,
          celular:         this.frmEditable.celular,
          api_response:    this.apiResponse,
        });
        if (res.status === 200) {
          this.$toast.add({ severity:'success', summary:'Conductor registrado', detail:`${this.nombreConductor} guardado correctamente`, life:4000 });
          this.$emit('guardado');
          this.cerrar();
        }
      } catch (err) {
        this.$toast.add({ severity:'error', summary:'Error al guardar', detail: err?.data?.message ?? 'No se pudo guardar el conductor', life:5000 });
      } finally {
        this.guardando = false;
      }
    },

    async guardarYCerrar() {
      this.guardando = true;
      const cookie = {};
      document.cookie.split(';').forEach(el => { const [k,v]=el.split('='); cookie[k.trim()]=decodeURIComponent(v??''); });
      try {
        let fechaStr = null;
        if (this.frm.fecha_expedicion) {
          const d = new Date(this.frm.fecha_expedicion);
          fechaStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
        const res = await window.api.call('post', '/guardar_conductor_hv', {
          email: cookie['Email'], id_rol: this.idRol, id_customer: this.idCustomer,
          documento: this.frm.documento.trim(),
          tipo_documento: this.frm.tipo_documento,
          fecha_expedicion: fechaStr,
          email_conductor: this.frmEditable.email,
          celular:         this.frmEditable.celular,
          api_response:    this.apiResponse,
        });
        if (res.status === 200) {
          this.$toast.add({ severity:'success', summary:'Resincronizado', detail:`${this.nombreConductor} actualizado correctamente`, life:4000 });
          this.$emit('guardado');
          this.cerrar();
        }
      } catch (err) {
        this.$toast.add({ severity:'error', summary:'Error al guardar', detail: err?.data?.message ?? 'No se pudo guardar la resincronización', life:5000 });
      } finally {
        this.guardando = false;
      }
    },

    cerrar() { this.visible = false; },

    resetModal() {
      this.detenerPollingSolicitudHv();
      this.paso = 1; this.submitted = false; this.consultando = false;
      this.guardando = false; this.yaExiste = false; this.errorConsulta = '';
      this.apiResponse = null; this.enProceso = false; this.sincronizacion = null;
      this.pollingIntento = 0;

      if (this.modoResincronizar) {
        const f = this.fechaExpedicionInicial;
        let fechaRestaurada = null;
        if (f && f !== 'null' && f.includes('-')) {
          const [y,m,d] = String(f).split('-');
          fechaRestaurada = new Date(Number(y), Number(m)-1, Number(d));
        }
        this.frm = { tipo_documento: this.tipoDocumentoInicial??1, documento: this.documentoInicial??'', fecha_expedicion: fechaRestaurada };
      } else {
        this.frm = { tipo_documento: 1, documento: '', fecha_expedicion: null };
      }
      this.frmEditable = { email: '', celular: '' };
      this.resetFotos();
    },

    fmtFechaISO(f) {
      if (!f) return '—';
      const [y,m,d] = String(f).substring(0,10).split('-');
      if (!y||!m||!d) return f;
      return `${d}/${m}/${y}`;
    },

    fmtCOP(valor) {
      if (!valor && valor !== 0) return '—';
      let v = typeof valor==='string' ? parseFloat(valor.replace(/\./g,'').replace(',','.')) : valor;
      if (isNaN(v)) return '—';
      return new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v);
    },
    // Agregar en methods
    async verFoto(key, urlEncriptada) {
        // Si ya está cargada, no volver a pedir
        if (this.fotosPublicas[key]) return;

        this.fotosLoading[key] = true;
        this.fotosError[key]   = null;

        const cookie = {};
        document.cookie.split(';').forEach(el => {
            const [k, v] = el.split('=');
            cookie[k.trim()] = decodeURIComponent(v ?? '');
        });

        try {
            const res = await window.api.call('post', '/img_public', {
                email:      cookie['Email'],
                id_rol:     this.idRol,
                imagenes:   [urlEncriptada],
                onlyimages: true,
            });

            // Estructura: res.data.data.images[0].blob
            const images = res.data?.data?.images ?? [];
            const blob   = images[0]?.blob ?? null;

            if (blob) {
                this.fotosPublicas[key] = blob; // ya viene como "data:image/jpeg;base64,..."
            } else {
                this.fotosError[key] = 'No se pudo obtener la imagen.';
            }
        } catch (err) {
            this.fotosError[key] = 'Error al cargar la imagen.';
        } finally {
            this.fotosLoading[key] = false;
        }
    },

    resetFotos() {
        this.fotosPublicas = {
            Foto_conductor_url:                       null,
            Foto_cedula_frontal_url:                  null,
            foto_cedula_anverso_url:                  null,
            foto_licencia_de_conduccion_frontal_url:  null,
            foto_licencia_de_conduccion_anverso_url:  null,
        };
        this.fotosLoading = { ...this.fotosPublicas };
        this.fotosError   = {};
    },
  },
};
</script>

<style scoped lang="scss">
$navy:#0A354E; $white:#fff; $g200:#e8eaed; $g500:#6b7280; $g700:#374151; $r:12px;

/* Dialog */
.mhc-dialog {
  :deep(.p-dialog-header) { font-weight:700; font-size:1rem; border-bottom:1px solid $g200; }
  :deep(.p-dialog-content){ padding:1.5rem; }
  :deep(.p-dialog-footer) { border-top:1px solid $g200; padding:1rem 1.5rem; }
}
.mhc-body { display:flex; flex-direction:column; gap:1rem; }

/* Buttons */
.mhc-btn-outline { display:inline-flex; align-items:center; gap:.3rem; background:$white; color:$g700; border:1px solid $g200; border-radius:8px; padding:.45rem .9rem; font-size:.82rem; font-weight:600; cursor:pointer; transition:all .15s; &:hover{border-color:$navy;color:$navy;} }
.mhc-btn-sm { padding:.3rem .7rem; font-size:.78rem; }
.mhc-btn-action { display:inline-flex; align-items:center; gap:.3rem; border:none; border-radius:8px; padding:.45rem 1rem; font-size:.82rem; font-weight:600; cursor:pointer; transition:background .15s; &:disabled{opacity:.6;cursor:not-allowed;} }
.mhc-btn-action--navy { background:$navy; color:#fff; &:hover:not(:disabled){background:#0c4f77;} }
.mhc-btn-action--warn { background:#ea580c; color:#fff; &:hover:not(:disabled){background:#c2410c;} }

/* Footer */
.mhc-footer { display:flex; justify-content:space-between; align-items:center; width:100%; }
.mhc-footer-right { display:flex; gap:.65rem; }

/* ── Estado en proceso ── */
.mhc-en-proceso { border:1px solid #bfdbfe; border-radius:$r; padding:1.5rem; background:#eff6ff; }
.mhc-en-proceso-header { display:flex; align-items:flex-start; gap:1rem; }
.mhc-en-proceso-icon { width:48px; height:48px; border-radius:12px; background:#dbeafe; display:flex; align-items:center; justify-content:center; flex-shrink:0; i{font-size:1.5rem;color:#2563eb;} }
.mhc-en-proceso-titulo { font-size:1rem; font-weight:700; color:#1e40af; }
.mhc-en-proceso-sub    { font-size:.82rem; color:#3b82f6; margin-top:3px; }
.mhc-en-proceso-chips  { display:flex; flex-wrap:wrap; gap:.5rem; margin-top:1rem; }
.mhc-chip { display:inline-flex; align-items:center; background:white; border:1px solid #bfdbfe; border-radius:20px; padding:3px 12px; font-size:.75rem; color:#1e40af; font-weight:500; }
.mhc-en-proceso-info { margin-top:.875rem; padding:.625rem .875rem; background:#dbeafe; border-radius:6px; font-size:.8rem; color:#1e40af; display:flex; align-items:center; }

/* Alertas */
.mhc-alert-warn { display:flex; align-items:center; flex-wrap:wrap; padding:.75rem 1rem; background:#fffbeb; border:1px solid #fde68a; border-radius:8px; margin-bottom:1rem; color:#92400e; font-size:.875rem; i{color:#d97706;} }

/* Formulario */
.mhc-form-card { border:1px solid #e5e7eb; border-radius:$r; padding:1.5rem; background:#fafbfc; }
.mhc-form-card--resinc { border-color:#fed7aa; background:#fff7ed; .mhc-form-header i{color:#ea580c;} .mhc-form-titulo{color:#9a3412;} }
.mhc-form-header { display:flex; align-items:flex-start; gap:1rem; margin-bottom:.5rem; i{font-size:2rem;color:$navy;flex-shrink:0;margin-top:2px;} }
.mhc-form-titulo { font-size:1rem; font-weight:700; color:#1e293b; }
.mhc-form-sub    { font-size:.8rem; color:#64748b; margin-top:2px; }
.mhc-form-grid   { display:grid; grid-template-columns:repeat(3,1fr); gap:.875rem; }
.mhc-form-field  { display:flex; flex-direction:column; gap:.3rem; label{font-size:.72rem;font-weight:700;color:$g500;text-transform:uppercase;letter-spacing:.5px;} }
.mhc-required { color:#ef4444; }
.mhc-error    { font-size:.72rem; color:#dc2626; }
.mhc-hint-lock { font-size:.72rem; color:$navy; }
.mhc-hint-gray { font-size:.72rem; color:#94a3b8; }
.mhc-loading { margin-top:1.25rem; }
.mhc-loading-text { margin-top:.75rem; font-size:.875rem; color:#475569; display:flex; align-items:center; }
.mhc-error-card { margin-top:1rem; padding:.875rem 1rem; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; color:#991b1b; font-size:.875rem; }

/* ── Header conductor paso 2 ── */
.mhc-conductor-header { display:flex; align-items:flex-start; gap:1.25rem; background:linear-gradient(135deg,#0A354E 0%,#0c4f77 100%); border-radius:$r; padding:1.25rem 1.5rem; color:white; flex-wrap:wrap; }
.mhc-conductor-foto { width:64px; height:64px; border-radius:50%; overflow:hidden; border:3px solid rgba(255,255,255,.3); flex-shrink:0; }
.mhc-conductor-foto-placeholder { width:64px; height:64px; border-radius:50%; background:rgba(255,255,255,.15); display:flex; align-items:center; justify-content:center; i{font-size:1.75rem;color:white;} }
.mhc-conductor-main   { flex:1; min-width:160px; }
.mhc-conductor-nombre { font-size:1.05rem; font-weight:700; margin-top:4px; }
.mhc-conductor-sub    { font-size:.75rem; color:rgba(255,255,255,.7); margin-top:3px; }
.mhc-doc-badge { display:inline-block; background:white; color:$navy; font-size:.9rem; font-weight:800; padding:3px 12px; border-radius:6px; letter-spacing:1px; font-family:monospace; }
.mhc-conductor-badges { display:flex; gap:1rem; flex-wrap:wrap; align-items:flex-start; }
.mhc-badge-item { display:flex; flex-direction:column; align-items:center; gap:4px; }
.mhc-badge-lbl  { font-size:.65rem; color:rgba(255,255,255,.7); font-weight:600; text-transform:uppercase; }

/* Status / count */
.mhc-status { display:inline-block; font-size:.7rem; font-weight:700; padding:.2rem .55rem; border-radius:20px; white-space:nowrap; }
.mhc-status--ok     { background:#f0fdf4; color:#16a34a; }
.mhc-status--danger { background:#fef2f2; color:#dc2626; }
.mhc-status--warn   { background:#fffbeb; color:#d97706; }
.mhc-status--sec    { background:#f1f5f9; color:#64748b; }
.mhc-count { display:inline-flex; align-items:center; justify-content:center; min-width:24px; height:24px; padding:0 6px; border-radius:12px; font-size:.78rem; font-weight:700; }
.mhc-count--ok     { background:#f0fdf4; color:#16a34a; }
.mhc-count--danger { background:#fef2f2; color:#dc2626; }
.mhc-count--warn   { background:#fffbeb; color:#d97706; }

/* Secciones */
.mhc-seccion-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:1rem; }
.mhc-seccion { border:1px solid #e5e7eb; border-radius:8px; padding:1rem; background:#fafbfc; }
.mhc-seccion-titulo { font-size:.72rem; font-weight:700; color:$navy; text-transform:uppercase; letter-spacing:.5px; margin-bottom:.75rem; display:flex; align-items:center; gap:6px; i{font-size:.78rem;} }
.mhc-kv-grid { display:grid; grid-template-columns:130px 1fr; gap:.35rem .5rem; font-size:.8rem; }
.mhc-kv-lbl  { font-weight:600; color:#64748b; }
.mhc-bold    { font-weight:700; }
.mhc-mono    { font-family:monospace; font-size:.79rem; color:$g700; }
.mhc-sub     { font-size:.78rem; color:$g500; }

/* Póliza */
.mhc-poliza-card { border:1px solid #e5e7eb; border-radius:8px; padding:1rem 1.25rem; margin-bottom:.75rem; background:#f9fafb; &.mhc-poliza-vigente{border-color:#86efac;background:#f0fdf4;} }
.mhc-poliza-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:.75rem; }
.mhc-poliza-entidad { font-weight:700; font-size:.85rem; color:$g700; }
.mhc-poliza-kv { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:.4rem 1rem; font-size:.8rem; div{display:flex;flex-direction:column;gap:1px;} }

/* SIMIT tabla */
.mhc-tabla { width:100%; border-collapse:collapse; font-size:.82rem; }
.mhc-tabla thead tr { background:#f8f9fa; }
.mhc-tabla th { text-align:left; padding:.55rem .75rem; font-size:.63rem; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:$g500; border-bottom:1px solid $g200; }
.mhc-tabla td { padding:.6rem .75rem; border-bottom:1px solid #f1f3f4; color:$g700; vertical-align:middle; }
.mhc-tabla tbody tr:last-child td { border-bottom:none; }
.mhc-tabla tbody tr:hover td { background:#fafbfc; }
.mhc-monto { color:#dc2626; font-weight:600; font-size:.8rem; }

/* SIMIT vacío */
.mhc-simit-clean { display:flex; flex-direction:column; align-items:center; padding:2rem 1rem; text-align:center; gap:.5rem; strong{color:#166534;} p{color:#4b7a60;font-size:.875rem;margin:0;} }
.mhc-simit-icon  { font-size:2.5rem; color:#22c55e; }
.mhc-deuda-resumen { display:flex; justify-content:space-around; flex-wrap:wrap; gap:.5rem; padding:.875rem 1.25rem; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; }
.mhc-deuda-item  { display:flex; flex-direction:column; align-items:center; gap:.3rem; }
.mhc-deuda-total { font-size:1rem; color:#dc2626; font-weight:700; }
.mhc-tab-badge   { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; background:#dc2626; color:white; border-radius:50%; font-size:.65rem; font-weight:700; margin-left:.4rem; }
.mhc-nota        { font-size:.72rem; color:#94a3b8; display:flex; align-items:center; gap:.4rem; i{font-size:.72rem;} }

/* Empty */
.mhc-empty-tab { display:flex; align-items:center; gap:.5rem; padding:2rem 1rem; color:#94a3b8; font-size:.875rem; i{font-size:1.25rem;} }

/* Responsive */
@media(max-width:768px) {
  .mhc-form-grid { grid-template-columns:1fr; }
  .mhc-conductor-header { flex-direction:column; }
  .mhc-seccion-grid { grid-template-columns:1fr; }
  .mhc-kv-grid { grid-template-columns:110px 1fr; }
  .mhc-footer { flex-direction:column; gap:.75rem; align-items:stretch; .mhc-footer-right{justify-content:flex-end;} }
}
/* ── Fotos / Documentos tab ── */
.mhc-fotos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}

.mhc-foto-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fafbfc;
}

.mhc-foto-card-header {
  display: flex;
  align-items: center;
  padding: .6rem .875rem;
  background: #f1f5f9;
  border-bottom: 1px solid #e5e7eb;
  font-size: .75rem;
  font-weight: 700;
  color: $navy;
  i { font-size: .78rem; }
}

.mhc-foto-preview {
  display: flex;
  flex-direction: column;
}

.mhc-foto-img {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
}

.mhc-foto-open {
  text-align: center;
  font-size: .72rem;
  color: $navy;
  font-weight: 600;
  padding: .4rem;
  background: #eff6ff;
  text-decoration: none;
  border-top: 1px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .3rem;
  &:hover { background: #dbeafe; }
}

.mhc-foto-placeholder {
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .4rem;
  color: #94a3b8;
  padding: 1rem;
  text-align: center;
  i    { font-size: 1.75rem; }
  span { font-size: .82rem; }
  small{ font-size: .7rem; color: #cbd5e1; }

  &--clickable {
    cursor: pointer;
    transition: background .15s;
    &:hover {
      background: #eff6ff;
      color: $navy;
      i { color: $navy; }
    }
  }
  &--loading { color: #3b82f6; i { color: #3b82f6; } }
  &--error   { color: #dc2626; i { font-size: 1.5rem; color: #dc2626; } }
}

.mhc-tab-badge--blue {
  background: #2563eb;  // override del rojo por defecto
}
</style>